// ============================================================
// Das Projekt-Cockpit (SPEC §39) — vier Leserouten und ein Haken.
//
// Die Übersicht eines Projekts zeigt oben, was die Runde schon weiß: Ziel,
// nächster Meilenstein, nächster Schritt, Puls (die Jetzt-Leiste), darunter
// „Offen", „Beschlüsse" und den eingeklappten „Verlauf". Alles LESEND und aus
// Daten, die es schon gibt (§39.2) — geschrieben wird hier nur eines: ein
// offener Punkt wird abgehakt, und zwar über die vorhandene Route SEINER Quelle
// mit ihren Rechten — ein Schritt der Runde über `PATCH /runden/punkte/{id}`
// (§18.5), seit §50 eine Aufgabe oder Klärung eines Termins über
// `POST /termine/punkte/{id}/erledigt` (§50.5). Eine zweite Schreibstelle für
// denselben Punkt gäbe es sonst neben der Runde bzw. dem Reiter Termine.
//
// Eine eigene Datei neben `hooks.ts` aus demselben Grund wie `freigaben.ts`:
// `hooks.ts` ist die meistgeänderte Datei des Frontends. Die Regeln aus ihrem
// Kopf gelten hier genauso — Serverdaten gehören TanStack Query, Pfade ohne
// `/api`.
//
// ---- Zwei Festlegungen --------------------------------------------------------
//
// 1. DIE SCHLÜSSEL LIEGEN UNTER `["cockpit", id]`, nicht unter
//    `["projekt", id]` (E39.2 sah das vor; geprüft und verworfen, §39.11). Die
//    Runde meldet sich im Ereignisstrom anweisungsweise und OHNE Projektkennung
//    (031) — ihr Haken muss also JEDES Cockpit treffen, und das geht nur mit
//    einem eigenen Präfix. `entwerten(qc, id)` nimmt `["cockpit", id]` mit
//    (hooks.ts), `lib/ereignisse.ts` die Tabellen aus §39.11.
//
// 2. DER VERLAUF LÄDT ERST BEIM AUFKLAPPEN (`aktiv`), Offen und Beschlüsse
//    ebenso erst bei „Alle n": die Jetzt-Leiste samt den ersten drei steht
//    schon in EINER Antwort (`useCockpit`). Ein Projektblatt, das beim Öffnen
//    vier Anfragen stellt, ist beim Blättern durch die Projekte (←/→) vier mal
//    so langsam.
//
// Namen, über die man stolpert: `useVerlauf` in `hooks.ts` ist der Verlauf der
// MONATSSTÄNDE (§20.3, Reiter Stand) — der Strom hier heißt `useCockpitVerlauf`
// und liegt unter `/projekte/{id}/cockpit/verlauf`, weil `/projekte/{id}/verlauf`
// schon jenem gehört.
// ============================================================
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api, frage } from "./client";
import { schluessel } from "./hooks";
import { punkteEntwerten } from "./terminpunkte";
import type {
  Beschluss,
  CockpitQuelle,
  OffenerPunkt,
  ProjektCockpit,
  RundePunkt,
  RundePunktAendern,
  TerminPunkt,
  TerminPunktErledigt,
  VerlaufSeite,
} from "./types";

/* ========================================================== Schlüssel */

/**
 * Das dritte Glied des Verlaufsschlüssels (`["cockpit", id, "verlauf"]`). Benannt, weil
 * `lib/ereignisse.ts` den Verlauf bei Rundenmeldungen AUSNIMMT (§39.11): eine laufende Runde
 * meldet bei jedem verlassenen Feld, und jede geladene Verlaufsseite rechnet den Strom aus
 * sechs Quellen neu. Kopf, Offen und Beschlüsse kommen weiter sofort.
 */
export const COCKPIT_VERLAUF_TEIL = "verlauf";

/** Alle unter dem Präfix `["cockpit", id]` (hooks.ts `schluessel.cockpit`) — ein
 *  `invalidateQueries` darauf trifft Kopf, Offen, Beschlüsse und Verlauf zugleich. */
export const cockpitSchluessel = {
  /** Jedes Cockpit jedes Projekts — für die Runde (Ereignis ohne Projektkennung) und den Haken. */
  alle: schluessel.cockpitAlle,
  /** Alles EINES Projekts. */
  projekt: (id: string) => schluessel.cockpit(id),
  kopf: (id: string) => [...schluessel.cockpit(id), "kopf"] as const,
  offen: (id: string) => [...schluessel.cockpit(id), "offen"] as const,
  beschluesse: (id: string) => [...schluessel.cockpit(id), "beschluesse"] as const,
  verlauf: (id: string) => [...schluessel.cockpit(id), COCKPIT_VERLAUF_TEIL] as const,
};

/* ========================================================== Zahlen */

/** Wie viele offene Punkte und Beschlüsse die Cockpit-Antwort mitbringt (§39.2) —
 *  dieselbe Zahl wie `COCKPIT_VORSCHAU` im Router. Mehr gibt es über „Alle n". */
export const COCKPIT_VORSCHAU = 3;
/** Einträge je Verlaufsseite (`anzahl`, §39.3); der Server nimmt 1 bis `VERLAUF_SEITE_MAX`. */
export const VERLAUF_SEITE = 20;
export const VERLAUF_SEITE_MAX = 50;

/** Wie lange eine Antwort als frisch gilt. Der Ereignisstrom entwertet ohnehin;
 *  das hier verhindert nur, dass jedes Zurückblättern zum Projekt neu fragt. */
const FRISCH_MS = 30_000;

/* ========================================================== Lesen */

/**
 * `GET /projekte/{id}/cockpit` → `ProjektCockpit`: Jetzt-Leiste, die ersten drei
 * offenen Punkte und Beschlüsse samt Zahlen, die Zahl des Verlaufs. 404, wenn es
 * das Projekt für diese Person nicht gibt (§36.3).
 */
export function useCockpit(projektId: string | null | undefined, aktiv = true): UseQueryResult<ProjektCockpit> {
  return useQuery({
    queryKey: cockpitSchluessel.kopf(projektId ?? ""),
    queryFn: () => api.get<ProjektCockpit>(`/projekte/${projektId}/cockpit`),
    enabled: !!projektId && aktiv,
    staleTime: FRISCH_MS,
  });
}

/**
 * `GET /projekte/{id}/offen` → ALLE offenen Punkte, ältere Fälligkeit zuerst
 * (§39.2). `aktiv` erst, wenn „Alle n" aufgeklappt ist — die ersten drei stehen
 * schon in `useCockpit`.
 */
export function useOffenePunkte(projektId: string | null | undefined, aktiv: boolean): UseQueryResult<OffenerPunkt[]> {
  return useQuery({
    queryKey: cockpitSchluessel.offen(projektId ?? ""),
    queryFn: () => api.get<OffenerPunkt[]>(`/projekte/${projektId}/offen`),
    enabled: !!projektId && aktiv,
    staleTime: FRISCH_MS,
  });
}

/**
 * `GET /projekte/{id}/beschluesse` → alle Beschlüsse, neueste zuerst (höchstens
 * 200, §39.10). `aktiv` wie bei `useOffenePunkte`.
 */
export function useBeschluesse(projektId: string | null | undefined, aktiv: boolean): UseQueryResult<Beschluss[]> {
  return useQuery({
    queryKey: cockpitSchluessel.beschluesse(projektId ?? ""),
    queryFn: () => api.get<Beschluss[]>(`/projekte/${projektId}/beschluesse`),
    enabled: !!projektId && aktiv,
    staleTime: FRISCH_MS,
  });
}

/**
 * `GET /projekte/{id}/cockpit/verlauf?vor=&anzahl=` — seitenweise, neueste
 * zuerst (§39.3). `aktiv` erst beim Aufklappen. „Mehr" ist `fetchNextPage`;
 * `hasNextPage` wird falsch, sobald eine Seite `weiter: null` bringt.
 *
 * Rundenmeldungen aus dem Strom entwerten ihn NICHT (§39.11, `COCKPIT_VERLAUF_TEIL`):
 * ein aufgeklappter Verlauf zeigt eine neue Rundenzeile erst beim nächsten Aufklappen
 * (nach `FRISCH_MS`) oder mit der nächsten Meldung zu DIESEM Projekt — hingenommen.
 *
 * Hausregel (§38.11): `null` aus dem Netz kann als `undefined` ankommen —
 * `?? undefined` macht aus beidem „keine weitere Seite".
 */
export function useCockpitVerlauf(
  projektId: string | null | undefined,
  aktiv: boolean,
): UseInfiniteQueryResult<InfiniteData<VerlaufSeite, string | null>> {
  return useInfiniteQuery({
    queryKey: cockpitSchluessel.verlauf(projektId ?? ""),
    queryFn: ({ pageParam }) =>
      api.get<VerlaufSeite>(`/projekte/${projektId}/cockpit/verlauf` + frage({ vor: pageParam, anzahl: VERLAUF_SEITE })),
    initialPageParam: null as string | null,
    getNextPageParam: (letzte: VerlaufSeite) => letzte.weiter ?? undefined,
    enabled: !!projektId && aktiv,
    staleTime: FRISCH_MS,
  });
}

/* ========================================================== Abhaken */

/** Was `useOffenAbhaken` braucht: Kennung, Quelle und Projekt des Punkts (aus `OffenerPunkt`) und
 *  die Richtung — `false` ist „Rückgängig" aus der Meldung (§39.5). */
export interface OffenAbhakenAuftrag {
  id: string;
  erledigt: boolean;
  /** §50.5: welche Route — `runde`: `PATCH /runden/punkte/{id}`; `termin`:
   *  `POST /termine/punkte/{id}/erledigt`. */
  quelle: CockpitQuelle;
  /** §50.5: `OffenerPunkt.projekt_id` — ein Termin-Punkt entwertet danach auch `["projekt", id]`
   *  (Reiter Termine, `Termin.punkte_zahl`) und die Terminlisten. */
  projekt_id: string;
}

/**
 * Abhaken über die Route der QUELLE, mit IHREN Rechten:
 * - `runde` → `PATCH /runden/punkte/{id}` `{erledigt}` → `RundePunkt` (§18.5, §39.4): Admin/GF,
 *   wessen Block der Punkt ist, wer verantwortlich ist, wer das Projekt bearbeiten darf. Auch in
 *   einer abgeschlossenen Runde erlaubt — dort geht NUR noch `erledigt`. Der unbesprochene
 *   Nachfolger in der Folgewoche geht mit (§39.4).
 * - `termin` → `POST /termine/punkte/{id}/erledigt` `{erledigt}` → `TerminPunkt` (§50.5): Team,
 *   Projektleitung, GF/Admin und wer verantwortlich ist.
 *
 * Die Oberfläche zeigt den Haken nur bei `OffenerPunkt.darf_abhaken`; ein 403 geht trotzdem als
 * Meldung hinaus (die Rechte können sich zwischen Lesen und Drücken ändern).
 *
 * Danach — auch nach einem Fehler, denn dann stimmt die Liste vielleicht nicht mehr — JEDES
 * Cockpit (ein Runde-Punkt kann in zwei offenen Projektblättern stehen) und jede Runde (`/runde`
 * und die Rundenkarte der Startseite zeigen dasselbe Häkchen); bei einem Termin-Punkt zusätzlich
 * die Akte samt Punktelisten und die Terminlisten (`punkteEntwerten`).
 */
export function useOffenAbhaken(): UseMutationResult<RundePunkt | TerminPunkt, Error, OffenAbhakenAuftrag> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, erledigt, quelle }: OffenAbhakenAuftrag): Promise<RundePunkt | TerminPunkt> =>
      quelle === "termin"
        ? api.post<TerminPunkt>(`/termine/punkte/${id}/erledigt`, { erledigt } satisfies TerminPunktErledigt)
        : api.patch<RundePunkt>(`/runden/punkte/${id}`, { erledigt } satisfies RundePunktAendern),
    onSettled: (_daten, _fehler, auftrag) => {
      void qc.invalidateQueries({ queryKey: cockpitSchluessel.alle });
      void qc.invalidateQueries({ queryKey: schluessel.rundenAlle });
      if (auftrag.quelle === "termin") punkteEntwerten(qc, auftrag.projekt_id);
    },
  });
}
